import React from "react";
import { Box, Typography, Link as MuiLink, useTheme } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import XIcon from '@mui/icons-material/X';

const Footer = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();
  const match = location.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)\b/);
  const prefix = match ? `/${match[1]}` : "";

  return (
    <Box
      id="app-footer"
      component="footer"
      sx={{
        mt: 4,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        overflow: "hidden",
        boxShadow: theme.palette.mode === "dark"
          ? "0 0 32px #49bc9080"
          : "0 0 20px #ffa84d33",
        mb: { xs: "60px", md: 0 },
      }}
    >
      {/* Top Row */}
      <Box
        sx={{
          bgcolor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          py: 2.5,
          textAlign: "center",
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
        }}
      >
        {/* Logo */}
        <Box sx={{ display: "flex", alignItems: "center", mr: { sm: 3, xs: 0 }, mb: { xs: 1, sm: 0 } }}>
          <Box
            component="img"
            src="/logo.png"
            alt="in-Cooking logo"
            sx={{
              height: 44,
              width: 44,
              mr: 1,
              borderRadius: "50%",
              bgcolor: "#fff",
              boxShadow: "0 0 12px #4dd6b360",
            }}
          />
          <Typography variant="h6" sx={{ color: "#fff", fontWeight: 900 }}>
            in-Cooking
          </Typography>
        </Box>
        {/* Social Links */}
        <Box sx={{ display: "flex", gap: 3, alignItems: "center", justifyContent: "center" }}>
          <MuiLink
            href="mailto:support@incooking.eu"
            color="inherit"
            underline="hover"
            sx={{ display: "flex", alignItems: "center", gap: 0.7 }}
          >
            <EmailIcon sx={{ color: "#fff" }} fontSize="medium" />
            <Typography variant="body2">{t("footer_email") || "Email"}</Typography>
          </MuiLink>
          <MuiLink
            href="https://www.facebook.com/share/1AJivziGMm/"
            color="inherit"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            aria-label="Facebook"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.7,
              '&:hover .MuiSvgIcon-root': { color: "#4267B2" }, // Facebook blue
            }}
          >
            <FacebookIcon sx={{ color: "#fff" }} fontSize="medium" />
            <Typography variant="body2">Facebook</Typography>
          </MuiLink>
          <MuiLink
            href="https://www.instagram.com/incooking_app/"
            color="inherit"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            aria-label="Instagram"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.7,
              '&:hover .MuiSvgIcon-root': {
                color: "#E4405F",
                background: "radial-gradient(circle at 33% 100%, #FFD676 0%, #E4405F 60%, #8A3AB9 100%)",
                borderRadius: "20%",
                boxShadow: "0 0 5px #e4405f50",
              }
            }}
          >
            <InstagramIcon sx={{ color: "#fff" }} fontSize="medium" />
            <Typography variant="body2">Instagram</Typography>
          </MuiLink>
          <MuiLink
            href="https://twitter.com/incooking_app"
            color="inherit"
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            aria-label="Twitter"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.7,
              '&:hover .MuiSvgIcon-root': { color: "#1DA1F2" }, // Twitter blue
            }}
          >
            <XIcon sx={{ color: "#fff" }} fontSize="medium" />
            <Typography variant="body2">Twitter</Typography>
          </MuiLink>
        </Box>
      </Box>
      {/* Bottom Row */}
      <Box
        sx={{
          bgcolor: theme.palette.mode === "dark" ? "#23272e" : "#f5f5f5",
          color: theme.palette.text.primary,
          py: 1.25,
          textAlign: "center",
          borderBottomLeftRadius: 24,
          borderBottomRightRadius: 24,
        }}
      >
        <Typography variant="caption" sx={{ opacity: 0.82, display: "block" }}>
          {t("footer_made_with_love") || "Made with love by the in-Cooking team"} |{" "}
          <MuiLink href={`${prefix}/about-us`} color="primary.main" underline="hover">
            {t("footer_about_us") || "About us"}
          </MuiLink>
        </Typography>
        <Typography
          variant="caption"
          sx={{
            opacity: 0.62,
            display: "block",
            mt: 0.5,
            mb: 0.2,
            lineHeight: 1.4
          }}
        >
          {t("footer.brainledger_prefix") || "BrainLedger is created and supported by"}{" "}
          <MuiLink
            href="https://brainstorm-visions.com"
            color="inherit"
            underline="hover"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontWeight: 500,
              transition: "0.2s",
              "&:hover": {
                opacity: 0.8,
                textDecoration: "underline"
              }
            }}
          >
            {t("footer.brand_name") || "Brainstorm Visions"}
          </MuiLink>
          .
        </Typography>

        <Typography
          variant="caption"
          sx={{
            opacity: 0.62,
            display: "block",
            mt: 0.3,
            lineHeight: 1.4
          }}
        >
          {t("footer.ecosystem") || "Part of the Brainstorm Visions ecosystem."}
        </Typography>
      </Box>
    </Box>
  );
};

export default Footer;
